import {create} from "zustand";
import {derivedRemoteStore} from "./DerivedStore";
import {KeyData, KeySnapshot, KeyVaultRef} from "./Model";
import {getOrSet} from "../util/MapUtil";
import {emptyArray} from "../util/FunctionUtil";
import {useKeyDataStore} from "./KeyDataStore";
import {naturalCmpOptions, stringCmp} from "../util/SortUtil";

export function normalizeNfcTransponderId(nfc: string): string {
    const ucNFC = nfc.toUpperCase();
    if (ucNFC.length === 14) {
        return ucNFC.substring(0, 8);
    } else if (ucNFC.length === 8) {
        return ucNFC;
    } else {
        return ucNFC;
    }
}

export class VaultKeys {
    private readonly _allVaultKeys = new Map<KeyVaultRef['id'], KeySnapshot[]>();
    private readonly _withPersonKeys = new Map<KeyVaultRef['id'], KeySnapshot[]>();
    private readonly _inVaultKeys = new Map<KeyVaultRef['id'], KeySnapshot[]>();
    private readonly _nfcKeys = new Map<string, KeySnapshot>();
    private readonly _allKeys: KeySnapshot[] = [];

    constructor(keys: KeyData[]) {
        this._allKeys = keys.flatMap(key =>
            key.history
                .filter(record => !record.to)
                .map<KeySnapshot>(historyRecord => ({key, historyRecord}))
                .sort(stringCmp(it => it.key.keyNumber, false, naturalCmpOptions))
        );

        this._allKeys.forEach(keyState => {
            getOrSet(this._allVaultKeys, keyState.historyRecord.vault.id, emptyArray).push(keyState);
            if (keyState.historyRecord.location.type === 'with-employee' || keyState.historyRecord.location.type === 'with-guest') {
                getOrSet(this._withPersonKeys, keyState.historyRecord.vault.id, emptyArray).push(keyState);
            }
            if (keyState.historyRecord.location.type === 'with-vault') {
                getOrSet(this._inVaultKeys, keyState.historyRecord.vault.id, emptyArray).push(keyState);
            }
            if (keyState.key.transponderId) {
                this._nfcKeys.set(normalizeNfcTransponderId(keyState.key.transponderId), keyState);
            }
        });
    }

    allKeys(): KeySnapshot[] {
        return this._allKeys;
    }

    allVaultKeys(keyVault: KeyVaultRef): KeySnapshot[] {
        return this._allVaultKeys.get(keyVault.id) || [];
    }

    withPerson(keyVault: KeyVaultRef): KeySnapshot[] {
        return this._withPersonKeys.get(keyVault.id) || [];
    }

    inVault(keyVault: KeyVaultRef): KeySnapshot[] {
        return this._inVaultKeys.get(keyVault.id) || [];
    }

    byKeyNumber(keyNumber: string | undefined): KeySnapshot[] {
        const result: KeySnapshot[] = [];
        if (keyNumber !== undefined) {
            this._allVaultKeys.forEach(vaultKeys => {
                vaultKeys.forEach(keyState => {
                    if (keyState.key.keyNumber === keyNumber) {
                        result.push(keyState);
                    }
                });
            });
        }
        return result;
    }

    byNFC(transponderId: string): KeySnapshot | undefined {
        return this._nfcKeys.get(normalizeNfcTransponderId(transponderId));
    }
}

export const useVaultKeysStore = create(
    derivedRemoteStore(
        {keyData: useKeyDataStore},
        ({keyData}) => new VaultKeys(keyData),
    )
);
